import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import { useAuth } from '../../../hooks/useAuth';

const RegisterForm = () => {
  const navigate = useNavigate();
  const { register, isLoading } = useAuth();
  const [formData, setFormData] = useState({
    displayName: '',
    email: '',
    password: '',
    confirmPassword: '',
    gradeLevel: '',
    agreeToTerms: false
  });
  const [errors, setErrors] = useState({});
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [submitError, setSubmitError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const gradeLevelOptions = [
    { value: '9', label: 'Grade 9' },
    { value: '10', label: 'Grade 10' },
    { value: '11', label: 'Grade 11' },
    { value: '12', label: 'Grade 12' },
    { value: 'graduate', label: 'Recent Graduate' },
    { value: 'other', label: 'Other' }
  ];

  const calculatePasswordStrength = (password) => {
    let strength = 0;
    if (password?.length >= 8) strength += 25;
    if (password?.length >= 12) strength += 25;
    if (/[a-z]/?.test(password) && /[A-Z]/?.test(password)) strength += 25;
    if (/[0-9]/?.test(password)) strength += 12.5;
    if (/[^a-zA-Z0-9]/?.test(password)) strength += 12.5;
    return strength;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e?.target;
    const newValue = type === 'checkbox' ? checked : value;
    
    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }));

    if (name === 'password') {
      setPasswordStrength(calculatePasswordStrength(value));
    }

    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
    
    if (submitError) {
      setSubmitError('');
    }
  };

  const handleSelectChange = (value) => {
    setFormData(prev => ({ ...prev, gradeLevel: value }));
    if (errors?.gradeLevel) {
      setErrors(prev => ({ ...prev, gradeLevel: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.displayName?.trim()) {
      newErrors.displayName = 'Display name is required';
    } else if (formData?.displayName?.trim()?.length < 2) {
      newErrors.displayName = 'Name must be at least 2 characters';
    }

    if (!formData?.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/?.test(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData?.password) {
      newErrors.password = 'Password is required';
    } else if (formData?.password?.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    if (!formData?.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData?.password !== formData?.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (!formData?.gradeLevel) {
      newErrors.gradeLevel = 'Please select your grade level';
    }

    if (!formData?.agreeToTerms) {
      newErrors.agreeToTerms = 'You must agree to the terms and conditions';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) return;

    setSubmitError('');
    setSuccessMessage('');

    const result = await register(formData.email, formData.password, {
      displayName: formData.displayName,
      gradeLevel: formData.gradeLevel,
    });

    if (result.success) {
      if (result.requiresEmailConfirmation) {
        setSuccessMessage('Registration successful! Please check your email to confirm your account.');
        setFormData({
          displayName: '',
          email: '',
          password: '',
          confirmPassword: '',
          gradeLevel: '',
          agreeToTerms: false
        });
        setPasswordStrength(0);
        setTimeout(() => {
          navigate('/login-register');
        }, 3000);
      } else {
        setSuccessMessage('Registration successful! Redirecting to dashboard...');
        setTimeout(() => {
          navigate('/student-dashboard');
        }, 1500);
      }
    } else {
      setSubmitError(result.error || 'Registration failed. Please try again.');
    }
  };

  const getPasswordStrengthColor = () => {
    if (passwordStrength < 25) return 'bg-error';
    if (passwordStrength < 50) return 'bg-warning';
    if (passwordStrength < 75) return 'bg-accent';
    return 'bg-success';
  };

  const getPasswordStrengthLabel = () => {
    if (passwordStrength < 25) return 'Weak';
    if (passwordStrength < 50) return 'Fair';
    if (passwordStrength < 75) return 'Good';
    return 'Strong';
  };

  const isFormDisabled = isLoading;

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {submitError && (
        <div className="p-4 bg-error/10 border border-error/20 rounded-lg">
          <p className="text-sm text-error">{submitError}</p>
        </div>
      )}
      {successMessage && (
        <div className="p-4 bg-success/10 border border-success/20 rounded-lg">
          <p className="text-sm text-success">{successMessage}</p>
        </div>
      )}
      <Input
        label="How should we call you?"
        type="text"
        name="displayName"
        placeholder="e.g., Alex, John, Sarah"
        value={formData?.displayName}
        onChange={handleChange}
        error={errors?.displayName}
        disabled={isFormDisabled}
        required
      />
      <Input
        label="Email Address"
        type="email"
        name="email"
        placeholder="student@example.com"
        value={formData?.email}
        onChange={handleChange}
        error={errors?.email}
        disabled={isFormDisabled}
        autoComplete="email"
        required
      />
      <div>
        <Input
          label="Password"
          type="password"
          name="password"
          placeholder="Create a strong password"
          value={formData?.password}
          onChange={handleChange}
          error={errors?.password}
          disabled={isFormDisabled}
          autoComplete="new-password"
          required
        />
        {formData?.password && (
          <div className="mt-2">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-muted-foreground">Password Strength</span>
              <span className="text-xs font-semibold text-foreground">{getPasswordStrengthLabel()}</span>
            </div>
            <div className="w-full bg-muted rounded-full h-1.5 overflow-hidden">
              <div
                className={`h-full transition-all duration-300 ease-out ${getPasswordStrengthColor()}`}
                style={{ width: `${passwordStrength}%` }}
              ></div>
            </div>
          </div>
        )}
      </div>
      <Input
        label="Confirm Password"
        type="password"
        name="confirmPassword"
        placeholder="Re-enter your password"
        value={formData?.confirmPassword}
        onChange={handleChange}
        error={errors?.confirmPassword}
        disabled={isFormDisabled}
        autoComplete="new-password"
        required
      />
      <Select
        label="Grade Level"
        placeholder="Select your current grade"
        options={gradeLevelOptions}
        value={formData?.gradeLevel}
        onChange={handleSelectChange}
        error={errors?.gradeLevel}
        disabled={isFormDisabled}
        required
      />
      <div>
        <Checkbox
          label="I agree to the Terms of Service and Privacy Policy"
          name="agreeToTerms"
          checked={formData?.agreeToTerms}
          onChange={handleChange}
          error={errors?.agreeToTerms}
          disabled={isFormDisabled}
          required
        />
      </div>
      <Button
        type="submit"
        variant="default"
        fullWidth
        loading={isLoading}
        disabled={isFormDisabled}
        className="mt-6"
      >
        Create Account
      </Button>
    </form>
  );
};

export default RegisterForm;